import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Send } from "lucide-react";

export const QuoteDialog = ({ open, onOpenChange, route }) => {
  const { lang } = useApp();
  const [routes, setRoutes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", route_id: "", participants: 4,
    preferred_dates: "", extras: "", comments: "",
  });

  useEffect(() => {
    if (open) {
      setSent(null);
      api.get("/routes").then((r) => setRoutes(r.data));
      setForm((f) => ({ ...f, route_id: route?.route_id || "" }));
    }
  }, [open, route]);

  const submit = async () => {
    if (!form.full_name || !form.phone) {
      toast.error(lang === "es" ? "Necesitamos tu nombre y contacto" : "We need your name and contact");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", { ...form, participants: Number(form.participants) });
      setSent(data);
      toast.success(lang === "es" ? "¡Solicitud enviada! Te contactamos pronto." : "Request sent! We'll contact you soon.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const field = "w-full rounded-lg border border-[#E2DFD7] px-3 py-2.5 text-sm outline-none focus:border-[#6E7E56]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Cotiza tu ruta privada" : "Quote your private trip"}</DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="space-y-4" data-testid="quote-sent">
            <p className="text-sm text-[#3D4A43]">
              {lang === "es"
                ? `Recibimos tu solicitud (folio ${sent.quote_id}). Te responderemos con la cotización a la brevedad.`
                : `We received your request (ref ${sent.quote_id}). We'll send your quote shortly.`}
            </p>
            <a href={sent.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="quote-whatsapp-link"
              className="flex items-center justify-center gap-2 w-full rounded-full bg-[#6E7E56] text-white font-semibold py-3 hover:bg-[#D94E28] transition-colors duration-300">
              <MessageCircle className="h-4 w-4" />
              {lang === "es" ? "Enviar también por WhatsApp" : "Also send via WhatsApp"}
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="quote-name" placeholder={lang === "es" ? "Nombre completo *" : "Full name *"}
                value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={field} />
              <input data-testid="quote-phone" placeholder={lang === "es" ? "Número de contacto *" : "Contact number *"}
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
              <input data-testid="quote-email" placeholder={lang === "es" ? "Correo (opcional)" : "Email (optional)"}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
              <input data-testid="quote-participants" type="number" min="1"
                placeholder={lang === "es" ? "Número de personas" : "Number of people"}
                value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} className={field} />
            </div>
            <select data-testid="quote-route" value={form.route_id}
              onChange={(e) => setForm({ ...form, route_id: e.target.value })} className={field}>
              <option value="">{lang === "es" ? "Ruta a realizar (o por definir)" : "Route (or to be defined)"}</option>
              {routes.map((r) => <option key={r.route_id} value={r.route_id}>{r.name}</option>)}
            </select>
            <input data-testid="quote-dates" placeholder={lang === "es" ? "Fechas tentativas (ej. 12–14 de octubre)" : "Tentative dates"}
              value={form.preferred_dates} onChange={(e) => setForm({ ...form, preferred_dates: e.target.value })} className={field} />
            <input data-testid="quote-extras" placeholder={lang === "es" ? "Servicios extra (lámparas, box lunch, transporte...)" : "Extra services"}
              value={form.extras} onChange={(e) => setForm({ ...form, extras: e.target.value })} className={field} />
            <textarea data-testid="quote-comments" rows={3} placeholder={lang === "es" ? "Comentarios adicionales" : "Additional comments"}
              value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} className={field} />
            <button onClick={submit} disabled={busy} data-testid="quote-submit-btn"
              className="flex items-center justify-center gap-2 w-full rounded-full bg-[#D94E28] text-white font-semibold py-3 hover:bg-[#B83B1A] disabled:opacity-50 transition-colors duration-300">
              <Send className="h-4 w-4" />
              {lang === "es" ? "Enviar solicitud de cotización" : "Send quote request"}
            </button>
            <p className="text-xs text-[#8A968E]">
              {lang === "es"
                ? "Tu solicitud llega a nuestro correo de contacto y también puedes enviarla por WhatsApp al terminar."
                : "Your request reaches our contact inbox and you can also send it via WhatsApp afterwards."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
